<script setup lang="ts">
import { computed } from 'vue';

import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { AccommodationSelectorType } from '@apps/main/new-desktop/modules/accommodationSelector';
import { AccommodationSelector } from '@apps/main/new-desktop/modules/accommodationSelector';

import { AppSnapScroll } from '@libs/ui';

import type { AvailableItineraryAccommodationSegment } from '../../../models';
import { ProductCardTopBarType } from '../../../models';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ChangeProductLink from '../ChangeProductLink.vue';

import AccommodationSegmentHeader from './AccommodationSegmentHeader.vue';
import AlternativeHotelCardMobile from './mobile/AlternativeHotelCardMobile.vue';
import AccommodationProductCardMobile from './mobile/AccommodationProductCardMobile.vue';
import GenericHotelCardMobile from './mobile/GenericHotelCardMobile.vue';
import AccommodationCarouselDesktop from './AccommodationCarouselDesktop.vue';

interface AvailableAccommodationSegmentProps {
  segment: AvailableItineraryAccommodationSegment;
  printMode: boolean;
  ingredientIndex: number;
}

const props = defineProps<AvailableAccommodationSegmentProps>();

interface AvailableAccommodationSegmentEmits {
  (eventName: 'add-filter'): void;
  (eventName: 'hotel-selection-navigation-triggered'): void;
  (eventName: 'remove-filter', id: HotelFilter): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'show-rooms'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (eventName: 'toggle-accommodation', value: AccommodationSelectorType): void;
}

const emit = defineEmits<AvailableAccommodationSegmentEmits>();

const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();

//NOTE: duplicated
const currentSelectedOrderType = computed<
  AccommodationSelectorType | undefined
>(() => {
  if (props.segment.productCardTopBarType === ProductCardTopBarType.Selected) {
    return 'popularAccommodation';
  }

  return props.segment.productCardTopBarType ===
    ProductCardTopBarType.Suggestion
    ? 'popularAccommodation'
    : 'cheapestAccommodation';
});

function onNavigationTriggered(): void {
  statsigEventsStore.logTripOverviewSeeAllHotelsClicked();
  emit('hotel-selection-navigation-triggered');
}
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    class="available-accommodation-segment"
  >
    <template #dropdown>
      <AccommodationSelector
        v-if="currentSelectedOrderType && !props.printMode"
        :is-there-selected-accommodation="
          props.segment.productCardTopBarType === ProductCardTopBarType.Selected
        "
        :selected-option="currentSelectedOrderType"
        :disabled="props.printMode"
        @toggle-accommodation="emit('toggle-accommodation', $event)"
      />
    </template>
    <template #header>
      <AccommodationSegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        @add-filter="emit('add-filter')"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <div class="content-wrapper">
        <div
          v-if="browserStore.isSmallerThan600px && !props.printMode"
          class="accommodation-cards-mobile-wrapper"
        >
          <AppSnapScroll direction="X" class="accommodation-cards-mobile">
            <template #scrollable>
              <AccommodationProductCardMobile
                class="card"
                :product-card-data="props.segment.productCardData"
                :context="props.segment.context"
                @show-details="emit('show-details')"
                @show-rooms="emit('show-rooms')"
              />
              <AlternativeHotelCardMobile
                v-for="hotel in props.segment.alternativeHotels"
                :key="hotel.hotelCode"
                class="card"
                :hotel="hotel"
                :ingredient-index="props.segment.context.ingredientIndex"
                :date="props.segment.productCardData.date"
              />
              <GenericHotelCardMobile
                class="card"
                :ingredient-index="props.segment.context.ingredientIndex"
              />
            </template>
          </AppSnapScroll>
          <ChangeProductLink
            class="change-product-link"
            :link-context="{
              segmentType: props.segment.segmentType,
              ingredientIndex: props.ingredientIndex,
            }"
            @navigation-triggered="emit('hotel-selection-navigation-triggered')"
          />
        </div>
        <template v-if="!browserStore.isSmallerThan600px">
          <AccommodationCarouselDesktop
            :segment="props.segment"
            :print-mode="props.printMode"
          >
            <ChangeProductLink
              v-if="!props.printMode"
              class="change-product-link"
              :link-context="{
                segmentType: props.segment.segmentType,
                ingredientIndex: props.ingredientIndex,
              }"
              @navigation-triggered="onNavigationTriggered"
            />
          </AccommodationCarouselDesktop>
        </template>
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.available-accommodation-segment {
  .content-wrapper {
    display: flex;
    flex-flow: column;
    gap: var(--spacing-16);

    .change-product-link {
      align-self: flex-end;
    }

    .accommodation-cards-mobile-wrapper {
      display: grid;
      gap: var(--spacing-16);

      .change-product-link {
        margin-left: auto;
      }

      .accommodation-cards-mobile {
        display: grid;
        grid-auto-flow: column;
        overscroll-behavior-x: contain;

        .card {
          margin-right: var(--spacing-12);

          &:last-child {
            margin-right: 0;
          }
        }
      }
    }
  }
}
</style>
